import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useHomeownerAuth } from "@/context/HomeownerAuthContext";
import SiteHeader from "@/components/SiteHeader";

function HomeownerAuthShell({ children, title, sub }) {
  return (
    <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF] flex flex-col">
      <SiteHeader />
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex bg-[#010204] text-[#EEEEEF] p-14 items-center grain relative overflow-hidden">
          <div className="max-w-md relative z-10">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
              — Homeowner
            </div>
            <h1 className="font-editorial text-6xl xl:text-7xl font-light tracking-tighter leading-[0.9]">
              Your projects.
              <br /><span className="italic">One dashboard.</span>
            </h1>
            <p className="mt-8 text-[#C9C6C6] leading-relaxed">
              Track every request from submitted to complete. See which pros
              have quoted, message them directly, and sign off when the work
              is done.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center p-8 md:p-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md"
          >
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-4">— {sub}</div>
            <h2 className="font-editorial text-4xl sm:text-5xl font-light tracking-tighter leading-[0.95] break-word-safe">{title}</h2>
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export function HomeownerSignup() {
  const { user, loading, signup } = useHomeownerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  if (loading) return null;
  if (user) return <Navigate to="/homeowner/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await signup(form);
      toast.success("Welcome! Your dashboard is ready.");
      navigate("/homeowner/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  const inputCls = "mt-2 bg-transparent border-x-0 border-t-0 border-b border-[#C9C6C6]/25 rounded-none h-12 focus-visible:ring-0 focus-visible:border-[#0E93EC] px-0";
  const labelCls = "text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]";
  return (
    <HomeownerAuthShell title="Create your account." sub="Homeowner signup">
      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="homeowner-signup-form">
        <div>
          <Label className={labelCls}>Full name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 placeholder="Jane Doe" className={inputCls} data-testid="ho-signup-name" required />
        </div>
        <div>
          <Label className={labelCls}>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 placeholder="you@example.com" className={inputCls} data-testid="ho-signup-email" required />
        </div>
        <div>
          <Label className={labelCls}>Password <span className="normal-case tracking-normal text-[#C9C6C6] font-normal">(min 8 chars)</span></Label>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                 placeholder="••••••••" className={inputCls} data-testid="ho-signup-password" required />
        </div>
        <Button type="submit" disabled={busy}
                className="group w-full bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-6 pr-5 text-base font-medium"
                data-testid="ho-signup-submit">
          <span className="flex-1 text-left">{busy ? "Creating…" : "Create account"}</span>
          <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
            <ArrowRight className="w-4 h-4" />
          </span>
        </Button>
        <p className="text-sm text-[#C9C6C6]">
          Already have an account?{" "}
          <Link to="/homeowner/login" className="text-[#0E93EC] underline underline-offset-4">Log in</Link>
        </p>
      </form>
    </HomeownerAuthShell>
  );
}

export function HomeownerLogin() {
  const { user, loading, login } = useHomeownerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  if (loading) return null;
  if (user) return <Navigate to="/homeowner/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form);
      navigate("/homeowner/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally { setBusy(false); }
  };

  const inputCls = "mt-2 bg-transparent border-x-0 border-t-0 border-b border-[#C9C6C6]/25 rounded-none h-12 focus-visible:ring-0 focus-visible:border-[#0E93EC] px-0";
  const labelCls = "text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]";
  return (
    <HomeownerAuthShell title="Welcome back." sub="Homeowner login">
      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="homeowner-login-form">
        <div>
          <Label className={labelCls}>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 placeholder="you@example.com" className={inputCls} data-testid="ho-login-email" required />
        </div>
        <div>
          <Label className={labelCls}>Password</Label>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                 placeholder="••••••••" className={inputCls} data-testid="ho-login-password" required />
        </div>
        <Button type="submit" disabled={busy}
                className="group w-full bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-6 pr-5 text-base font-medium"
                data-testid="ho-login-submit">
          <span className="flex-1 text-left">{busy ? "Signing in…" : "Sign in"}</span>
          <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
            <ArrowRight className="w-4 h-4" />
          </span>
        </Button>
        <p className="text-sm text-[#C9C6C6]">
          New here?{" "}
          <Link to="/homeowner/signup" className="text-[#0E93EC] underline underline-offset-4">Create an account</Link>
        </p>
      </form>
    </HomeownerAuthShell>
  );
}
